import { useEffect } from "react";

export const Config = {
  Providers: {
    OpenSearch: 'opensearch'
  },
  Layouts: {
    Standard: 'standard',
    Compact: 'compact',
    Horizontal: 'horizontal',
  },
  ViewModes: {
    Card: 'card',
    Accordion: 'accordion',
    ProfileTeaser: 'profile-teaser',
    Table: 'table',
  },
  FacetWidgets: {
    // Default (empty widget value) renders an inline checkbox list — see facetWidgetOptions.
    Multiselect: 'multiselect',    // dropdown panel of checkboxes (multi-select)
    Dropdown: 'dropdown',          // native <select> (single-select)
  },
  Mapping: {
    Card: {
      Featured: 'featured',
      Badges: 'badges',
      Pretitle: 'pretitle',
      Title: 'title',
      Subtitle: 'subtitle',
      ImageURL: 'imageURL',
      Body: 'body',
      Links: 'links',
    },
    Accordion: {
      ImageURL: 'imageURL',
      Title: 'title',
      Badge: 'badge',
      Body: 'body',
      Markdown: 'markdown',
      ExtraFields: 'extraFields',
      ExtraFieldLabels: 'extraFieldLabels',
      Links: 'links',
    },
    ProfileTeaser: {
      ImageURL: 'imageURL',
      Title: 'title',
      JobRole: 'jobRole',
      Location: 'location',
      Specialization: 'specialization',
      InstitutionName: 'institutionName',
      ProfilePageURL: 'profilePageURL',
      Links: 'links',
      ExtraFields: 'extraFields',
      ExtraFieldLabels: 'extraFieldLabels',
    }
  },
  // Per-column render type for the table view mode. Drives how each cell renders:
  // plain text, a sanitized link, or a list. See partials/search-results/table.
  ColumnTypes: {
    String: 'string',
    Number: 'number',
    Url: 'url',
    List: 'list',
    Links: 'links',
  },
  Table: {
    Styles: [
      ['', 'Default'],
      ['nv-table-bg-1', 'Background 1'],
      ['nv-table-bg-2', 'Background 2'],
      ['nv-table-alt-col-1', 'Alternating Columns 1'],
      ['nv-table-alt-col-2', 'Alternating Columns 2'],
      ['nv-table-alt-row-1', 'Alternating Rows 1'],
      ['nv-table-alt-row-2', 'Alternating Rows 2'],
    ]
  },
  DisplayOptions: {
    ItemsPerPage: {
      DefaultValue: 12,
      DefaultOptions: [12, 24, 36],
    }
  },
  I18n: {
    Filters: {
      BlockLabel: I18n.t('open_search.filters.block_label'),
      SortByLabel: I18n.t('open_search.filters.sort_by_label'),
      ClearAll: I18n.t('open_search.filters.clear_all'),
      RemoveFilter: (field, value) => I18n.t('open_search.filters.remove_filter', {field: field, value: value}),
    },
    Pager: {
      FirstPage: I18n.t('open_search.pagination.first_page'),
      LastPage: I18n.t('open_search.pagination.last_page'),
      NextPage: I18n.t('open_search.pagination.next_page'),
      PreviousPage: I18n.t('open_search.pagination.previous_page'),
      OfTotal: (total) => I18n.t('open_search.pagination.of_total', {total: total}),
    },
    ResultsPerPage: {
      Label: I18n.t('open_search.results_per_page.label'),
      Unit: I18n.t('open_search.results_per_page.unit'),
    },
    Stats: {
      FoundItems: (count) => I18n.t('open_search.stats.results_found', {count: count}),
    },
    Hits: {
      EmptyCell: I18n.t('open_search.stats.empty_cell'),
      NoResults: I18n.t('open_search.stats.no_results'),
      LearnMore: I18n.t('open_search.hits.learn_more'),
      Featured: I18n.t('open_search.hits.featured'),
    },
    SearchBox: {
      Placeholder: I18n.t('open_search.search_box.placeholder'),
    },
    ProfileTeaser: {
      ResearchFocus: I18n.t('profile_teasers.research_focus'),
      ProfilePage: I18n.t('profile_teasers.profile_page'),
    }
  }
}

// Single source of truth for displayOptions defaults — reused by the settings form
// (initialValues + normalizeSettings backfill) and the runtime view, so they can't
// drift across those layers.
export const DISPLAY_OPTION_DEFAULTS = {
  layout: 'standard',
  showFacetsCount: false,
  stickyNavigation: false,
  hideCategoryLabel: false,
  showSearchBox: true,
  searchFields: '',
  itemsPerPage: Config.DisplayOptions.ItemsPerPage.DefaultValue,
};

// Parse a facet's newline-separated display-order setting into an array of values.
export function parseDisplayOrder(order) {
  return `${order || ''}`.split('\n').map(value => value.trim()).filter(Boolean);
}

// Order Search UI facet options by `customOrder`; values not listed keep their default
// order, after the listed ones (so a short/empty order never drops values).
export function sortByDisplayOrder(options, customOrder) {
  const rank = (value) => {
    const index = customOrder.indexOf(value);
    return index === -1 ? customOrder.length : index;
  };
  return [...options].sort((a, b) => rank(a.value) - rank(b.value));
}

export function providerConfig(options) {
  const config = {
    alwaysSearchOnInitialLoad: true,
    initialState: {},
    searchQuery: {
      sort: [],
      filters: [],
      result_fields: {},
      facets: {},
      disjunctiveFacets: []
    },
  };

  if (options?.filters) {
    try {
      config.searchQuery.filters = JSON.parse(options.filters);
    } catch (e) {
      console.log('Invalid filters provided');
    }
  }

  if (options?.displayOptions) {
    config.initialState.resultsPerPage = options?.displayOptions?.itemsPerPage || Config.DisplayOptions.ItemsPerPage.DefaultValue;
  }

  // Sorting
  if (options?.sorting && Array.isArray(options.sorting) && options.sorting.length > 0) {
    const firstOption = options.sorting[0];

    config.initialState.sortField = firstOption.field;
    config.initialState.sortDirection = firstOption.direction;
  }

  // Facets
  if (options?.facets && Array.isArray(options.facets)) {
    for (const facetConfig of options.facets) {
      const {field} = facetConfig;
      config.searchQuery.facets[field] = {type: 'value', size: 20, sort: "value"}
      config.searchQuery.disjunctiveFacets.push(field);
    }
  }

  return config;
}

function validateOpenSearchConfig(config) {
  if (!config?.indexes || `${config.indexes}`.trim() === '') {
    return false;
  }

  if (config?.facets && Array.isArray(config.facets)) {
    for (const facet of config.facets) {
      const {field, label} = facet;
      if (!field || !label || `${field}`.trim() === '' || `${label}`.trim() === '') {
        return false;
      }
    }
  }

  return true;
}

function OpenSearchConfigs(providers = []) {
  return providers.filter(validateOpenSearchConfig);
}

function ExtractViewMode(settings) {
  if (!settings) return null;

  // New shape: per-mode config keyed by mode; the active mode is settings.viewMode.
  if (settings.viewMode && settings.viewModeConfigs) {
    const config = settings.viewModeConfigs[settings.viewMode];
    if (config) {
      // Spread the whole mode config so any mode's own fields survive (mapping/flags for
      // card/accordion, groupBy/groups for table). mapping keeps an empty-object default.
      return {...config, mapping: config.mapping || {}, viewMode: settings.viewMode};
    }
  }

  // Transitional: flat shape { viewMode, mapping, ...flags } from pre-nested builds.
  // Remove once such pages are re-saved into the nested viewModeConfigs shape.
  if (settings.viewMode && settings.mapping) {
    const {viewMode, mapping = {}, imagePadding, clickableCard, truncatePretitle} = settings;
    return {viewMode, mapping, imagePadding, clickableCard, truncatePretitle};
  }

  // Legacy back-compat (runtime read shim): published pages still store the old per-mode
  // arrays and are not auto-migrated. Read the first entry; the new key above wins when
  // present. Remove once all live pages are re-saved into the new shape.
  if (settings.cardViewModes?.[0]) {
    return {...settings.cardViewModes[0], viewMode: Config.ViewModes.Card};
  }

  if (settings.accordionViewModes?.[0]) {
    return {...settings.accordionViewModes[0], viewMode: Config.ViewModes.Accordion};
  }

  return null;
}

export function ExtractConfig(settings) {
  const configs = [];

  for (const config of OpenSearchConfigs(settings?.openSearchProviders)) {
    configs.push({...config, displayOptions: settings.displayOptions});
  }

  if (configs.length === 0) {
    return null;
  }

  const config = configs.pop();
  const viewMode = ExtractViewMode(config);
  if (viewMode) {
    config.ViewMode = viewMode;
  }

  if (settings?.compactMode) {
    config.compactMode = settings.compactMode;
  }

  return config;
}

export function uniqueTotalItems(rawResponse) {
  return rawResponse?.aggregations?.unique_count?.total_unique_count?.value || 0;
}

// Parse the comma-separated "Search fields" setting into a multi_match `fields` list.
// Entries pass through verbatim, so an explicit boost (`field^weight`) is honored and a
// bare field name gets OpenSearch's default boost of 1. Returns [] when none configured.
function parseSearchFields(searchFields) {
  return `${searchFields || ''}`
    .split(',')
    .map(field => field.trim())
    .filter(Boolean);
}

// Search-term query used when "Search fields" are configured. Replaces the
// connector's default query, whose hardcoded `phrase_prefix` clause OpenSearch
// rejects on keyword fields ("Can only use phrase prefix queries on text fields").
// Keyword-safe equivalents: `best_fields` (all terms within one field) + `cross_fields`
// (terms spread across fields, e.g. "jetson nano" matching title + description) keep
// relevance; `bool_prefix` (prefix query on the last term) gives "search as you type".
// `phrase`/`phrase_prefix` are intentionally omitted — they error on keyword fields.
// `lenient` skips fields the query can't apply to (e.g. numeric) instead of failing.
function buildSearchTermQuery(searchTerm, fields) {
  // Reachable (not dead code): the connector short-circuits only on a falsy searchTerm,
  // so whitespace-only input (truthy) still routes here. Trim and match everything rather
  // than building a `query: ""` multi_match, which would return zero results for "  ".
  const term = `${searchTerm ?? ''}`.trim();
  if (!term) {
    return {match_all: {}};
  }

  // Field names without the optional `^boost` suffix, for the prefix clauses below.
  const fieldNames = fields.map(field => field.split('^')[0]);

  return {
    bool: {
      minimum_should_match: 1,
      should: [
        {multi_match: {query: term, fields, type: 'best_fields', operator: 'and', lenient: true}},
        {multi_match: {query: term, fields, type: 'cross_fields', operator: 'and', lenient: true}},
        {multi_match: {query: term, fields, type: 'bool_prefix', lenient: true}},
        // Case-insensitive prefix per field so keyword fields (which an analyzer doesn't
        // lowercase) match regardless of case, e.g. "omni" → "OmniVision". `case_insensitive`
        // requires OpenSearch 1.0+; on text fields it prefix-matches the lowercased terms.
        ...fieldNames.map(name => ({prefix: {[name]: {value: term, case_insensitive: true}}})),
      ],
    },
  };
}

export function connectorConfig(host, indexes, aggregation, searchFields) {
  const config = {
    host: host,
    index: indexes,
  };

  config.connectionOptions = {
    headers: {
      'X-CSRF-Token': getCsrfToken(),
    },
  };

  if (aggregation && `${aggregation}`.trim() !== '') {
    config.connectionOptions.headers['x-dedup-field'] = aggregation;
  }

  const previewTokenEl = document.getElementById('library-preview-token');
  if (previewTokenEl) {
    const token = previewTokenEl.dataset?.value;
    if (token) {
      config.connectionOptions.headers['x-preview-token'] = token;
    }
  }

  // Only override the query when fields are configured; otherwise leave the
  // connector's default matching untouched (backward compatible).
  const fields = parseSearchFields(searchFields);
  if (fields.length > 0) {
    config.getQueryFn = (state) => buildSearchTermQuery(state?.searchTerm, fields);
  }

  return config;
}

export function useClickOutside(ref, handler) {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        handler(event.target);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, handler]);
}

export function hiddenFacets(facets) {
  if (!facets || !Array.isArray(facets)) return [];

  return facets.filter(item => item.hidden).map(item => item.field);
}
